import { useEffect, useMemo, useState } from "react";
import { PriceHeader } from "./components/PriceHeader";
import { RsiPanel } from "./components/RsiPanel";
import { BbPanel } from "./components/BbPanel";
import { SignalBanner } from "./components/SignalBanner";
import { PriceChart } from "./components/PriceChart";
import { RecentSignals } from "./components/RecentSignals";
import { ConfidentialBanner } from "./components/ConfidentialBanner";
import { ConfidentialityModal } from "./components/ConfidentialityModal";
import { useEntryConditions } from "./hooks/useEntryConditions";
import { useEntryConditionsHistory } from "./hooks/useEntryConditionsHistory";
import { useOhlc } from "./hooks/useOhlc";
import { useNow } from "./hooks/useNow";
import { Card } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Button } from "./components/ui/button";
import { Switch } from "./components/ui/switch";
import { Label } from "./components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "./components/ui/tabs";

const DEFAULT_SYMBOL = "ETH-PERP-INTX";
const HISTORY_LOOKBACK_MS = 4 * 60 * 60 * 1000;
const HISTORY_STEP = "5s";
const INITIAL_BARS = 300;
const UPDATE_BARS = 10;

type Timeframe = "1m" | "5m" | "15m" | "30m";

const TIMEFRAMES: Timeframe[] = ["1m", "5m", "15m", "30m"];

const GRANULARITY: Record<Timeframe, string> = {
  "1m": "1 minute",
  "5m": "5 minute",
  "15m": "15 minute",
  "30m": "30 minute",
};

const CONFIDENTIALITY_ACK_KEY = "pm:confidentiality-ack:v1";

export default function App() {
  const [symbol, setSymbol] = useState(DEFAULT_SYMBOL);
  const [inputSymbol, setInputSymbol] = useState(DEFAULT_SYMBOL);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [timeframe, setTimeframe] = useState<Timeframe>("1m");

  const [acknowledged, setAcknowledged] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    try {
      return window.localStorage.getItem(CONFIDENTIALITY_ACK_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [noticeOpen, setNoticeOpen] = useState(false);

  useEffect(() => {
    if (!acknowledged) return;
    try {
      window.localStorage.setItem(CONFIDENTIALITY_ACK_KEY, "1");
    } catch {
      // ignore (private mode, quota, etc.)
    }
  }, [acknowledged]);

  const now = useNow(500);
  const ec = useEntryConditions(symbol, 1000);
  const history = useEntryConditionsHistory(symbol, {
    lookbackMs: HISTORY_LOOKBACK_MS,
    step: HISTORY_STEP,
    intervalMs: 15_000,
  });
  const granularity = useMemo(() => GRANULARITY[timeframe], [timeframe]);
  const ohlc = useOhlc(symbol, {
    granularity,
    initialBars: INITIAL_BARS,
    updateBars: UPDATE_BARS,
    intervalMs: 5_000,
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const s = inputSymbol.trim();
    if (s && s !== symbol) setSymbol(s);
  };

  return (
    <div className="mx-auto flex min-h-full max-w-[1600px] flex-col gap-2 p-3">
      <ConfidentialBanner onView={() => setNoticeOpen(true)} />

      <div className="grid gap-2 lg:grid-cols-[minmax(0,1fr)_auto]">
        <PriceHeader
          symbol={symbol}
          data={ec.data}
          lastUpdatedAt={ec.lastUpdatedAt}
          now={now}
          error={ec.error}
        />
        <Card className="flex flex-wrap items-center gap-3 px-3 py-2">
          <form onSubmit={onSubmit} className="flex gap-2">
            <Input
              value={inputSymbol}
              onChange={(e) => setInputSymbol(e.target.value)}
              aria-label="Symbol"
              spellCheck={false}
              className="h-8 w-40 font-mono-tabular text-xs"
            />
            <Button type="submit" variant="secondary" size="sm">
              Load
            </Button>
          </form>

          <Tabs
            value={timeframe}
            onValueChange={(v) => setTimeframe(v as Timeframe)}
          >
            <TabsList className="h-8">
              {TIMEFRAMES.map((tf) => (
                <TabsTrigger key={tf} value={tf}>
                  {tf}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="flex items-center gap-2">
            <Switch
              id="audio"
              checked={audioEnabled}
              onCheckedChange={setAudioEnabled}
            />
            <Label htmlFor="audio" className="cursor-pointer">
              Sound
            </Label>
          </div>
        </Card>
      </div>

      <SignalBanner
        data={ec.data}
        history={history.data}
        now={now}
        audioEnabled={audioEnabled}
      />

      <div className="grid gap-2 lg:grid-cols-2">
        <Card className="flex min-h-[540px] flex-col p-2">
          {ohlc.loading && !ohlc.data && (
            <div className="px-3 py-2 text-xs italic text-muted-foreground">
              Loading {timeframe} bars…
            </div>
          )}
          <PriceChart
            candles={ohlc.data}
            history={history.data}
            livePrice={ec.data?.current_price ?? null}
            fitKey={`${symbol}-${timeframe}`}
          />
        </Card>
        <div className="flex min-h-[540px] flex-col gap-2">
          <RsiPanel data={ec.data} history={history.data} />
          <BbPanel data={ec.data} />
          <RecentSignals history={history.data} now={now} />
        </div>
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-1 text-[11px] text-muted-foreground">
        <span>
          Symbol: <b className="text-foreground">{symbol}</b> · Chart TF:{" "}
          <b className="text-foreground">{timeframe}</b>
        </span>
        <span className="flex items-center gap-3">
          <span>
            {INITIAL_BARS} bars initial · {UPDATE_BARS}-bar updates · History
            step: {HISTORY_STEP}
          </span>
          <span aria-hidden className="opacity-40">
            ·
          </span>
          <button
            type="button"
            onClick={() => setNoticeOpen(true)}
            className="text-[hsl(var(--warn))] underline-offset-2 hover:underline"
          >
            Confidential — Project Martin
          </button>
        </span>
      </footer>

      <ConfidentialityModal
        open={!acknowledged}
        onClose={() => setAcknowledged(true)}
        requireAcknowledge
      />
      <ConfidentialityModal
        open={acknowledged && noticeOpen}
        onClose={() => setNoticeOpen(false)}
      />
    </div>
  );
}
